import type { Sheet, SheetProps } from "./schema";

export const sheetPresets = {
  A3: { widthMm: 420, heightMm: 297 },
  A4: { widthMm: 210, heightMm: 297 },
  Letter: { widthMm: 215.9, heightMm: 279.4 },
  "16:9": { widthMm: 320, heightMm: 180 },
  "4:3": { widthMm: 300, heightMm: 225 },
  "1:1": { widthMm: 250, heightMm: 250 },
} as const;
export type SheetPreset = keyof typeof sheetPresets;
export type SheetSizeChoice = SheetPreset | "Custom";
export function isSheetPreset(value: string): value is SheetPreset {
  return value in sheetPresets;
}
export const sheetSizeChoices: SheetSizeChoice[] = [
  "Custom",
  "A4",
  "Letter",
  "16:9",
  "4:3",
  "1:1",
  "A3",
];

export function sheetDimensionsMm(paper: SheetPreset, landscape: boolean) {
  const { widthMm, heightMm } = sheetPresets[paper];
  const short = Math.min(widthMm, heightMm);
  const long = Math.max(widthMm, heightMm);
  return {
    widthMm: landscape ? long : short,
    heightMm: landscape ? short : long,
  };
}

export function sheetPageDimensionsMm(
  sheet: Pick<Sheet, "props" | "meta">,
  fallback: "A3" | "A4",
  landscape: boolean,
) {
  if (sheet.props.paper !== "A3" && sheet.props.paper !== "A4") {
    const widthMm = Number(sheet.meta.paperWidthMm);
    const heightMm = Number(sheet.meta.paperHeightMm);
    if (
      widthMm >= 50 &&
      widthMm <= 2000 &&
      heightMm >= 50 &&
      heightMm <= 2000
    )
      return { widthMm, heightMm };
    if (isSheetPreset(sheet.props.paper))
      return sheetDimensionsMm(sheet.props.paper, landscape);
  }
  return sheetDimensionsMm(fallback, landscape);
}

export function blankSheetProps(
  name: string,
  paper: SheetPreset,
  landscape: boolean,
  scale = 0,
): SheetProps {
  if (!Number.isFinite(scale) || scale < 0)
    throw new Error("Choose a positive drawing scale, or Not to Scale.");
  const { widthMm, heightMm } = sheetDimensionsMm(paper, landscape);
  const unitsPerMm = 8 / 3;
  return {
    w: widthMm * unitsPerMm,
    h: heightMm * unitsPerMm,
    name: name.trim() || "Blank sheet",
    paper,
    page: 1,
    source: "",
    preview: "",
    revision: "01",
    metersPerUnit: scale / (1000 * unitsPerMm),
    calibration: scale ? `Blank sheet · 1:${scale}` : "Not to Scale",
  };
}
